import { createTheme as MUIcreateTheme } from '@mui/material';

import colors, { typography } from './colors';

const createTheme = ({ parentTheme = MUIcreateTheme(), app }) => {
    if (!colors.themes[app]) app = 'cultivio';

    const fontFamily = typography.themes?.[app]?.fontFamily;
    const themeColors = colors.themes?.[app];

    /** @type {import('@mui/material').ThemeOptions} */
    const themeOptions = {
        palette: {
            primary: {
                main: themeColors?.primary.default,
                onSurface: themeColors?.mui.primary.onSurface,
                infoBackground: themeColors?.mui.primary.infoBackground,
                ...themeColors?.primary,
                mui: themeColors?.mui.primary.colors,
                light: themeColors?.primary[300],
                dark: themeColors?.primary[600],
                itemSelectedBackground: themeColors?.mui?.primary?.itemSelectedBackground,
            },
            secondary: {
                mui: {
                    secondary: themeColors?.mui.secondary.secondary,
                    onSurface: themeColors?.mui.secondary.onSurface,
                    variant: themeColors?.mui.secondary.variant,
                    snackbarVariant: themeColors?.mui.secondary.snackbarVariant,
                    colors: {
                        active: themeColors?.mui.secondary.colors.active,
                        inactive: themeColors?.mui.secondary.colors.inactive,
                        disabled: themeColors?.mui.secondary.colors.disabled,
                        stroke: themeColors?.mui.secondary.colors.stroke,
                        base: themeColors?.mui.secondary.colors.base,
                    },
                },
                main: themeColors?.secondary.default,
                ...themeColors?.secondary,
                light: themeColors?.secondary[300],
                dark: themeColors?.secondary[600],
            },
            dekalb: {
                main: colors.themes.dekalb.default,
                ...colors.themes.dekalb,
            },
            accent: {
                main: themeColors?.accent.default,
            },
            warning: {
                main: themeColors?.warning.default,
                light: themeColors?.warning[200],
                dark: themeColors?.warning[600],
            },
            success: {
                main: themeColors?.success.default,
                ...colors.themes.success,
            },
            text: {
                accent: themeColors?.primary.default,
                secondary: themeColors?.accent.default,
            },
            white: {
                main: '#FFF',
                dark: '#FFF',
                light: '#FFF',
            },
            gray: {
                main: colors.gray[800],
                contrastText: '#fff',
                ...colors.gray,
            },
            error: {
                ...colors.error,
            },
        },
        typography: {
            fontFamily: fontFamily,
            h1: {
                fontFamily: fontFamily,
                fontSize: '96px !important', // 96px
                fontWeight: '300', // 300
                letterSpacing: '-2px', // -2px
                lineHeight: '116px', // 116px
                fontStyle: 'normal', // normal
            },
            h2: {
                fontFamily: fontFamily,
                fontSize: '60px !important', // 60px
                fontWeight: '300', // 300
                letterSpacing: '-1px', // -1px
                lineHeight: '72px', // 72px
                fontStyle: 'normal', // normal
            },
            h3: {
                fontFamily: fontFamily,
                fontSize: '48px !important', // 48px
                fontWeight: '500', // 500
                letterSpacing: '-0.12px', // -0.12px
                lineHeight: '56px', // 56px
                fontStyle: 'normal', // normal
            },
            h4: {
                fontFamily: fontFamily,
                fontSize: '34px !important', // 34px
                fontWeight: '500', // 500
                lineHeight: '40px', // 40px
                fontStyle: 'normal', // normal
            },
            h5: {
                fontFamily: fontFamily,
                fontSize: '24px !important', // 24px
                fontWeight: '500', // 500
                lineHeight: '28px', // 28px
                fontStyle: 'normal', // normal
            },
            h6: {
                fontFamily: fontFamily,
                fontSize: '20px !important', // 20px
                fontWeight: '500', // 500
                letterSpacing: '0.25px', // 0.25px
                lineHeight: '24px', // 24px
                fontStyle: 'normal', // normal
            },
            subtitle1: {
                fontFamily: fontFamily,
                fontSize: '16px !important', // 16px
                fontWeight: '400', // 500
                letterSpacing: '0.25px', // 0.25px
                lineHeight: '24px', // 24px
                fontStyle: 'normal', // normal
            },
            subtitle2: {
                fontFamily: fontFamily,
                fontSize: '14px !important', // 14px
                fontWeight: '400', // 500
                letterSpacing: '0.25px', // 0.25px
                lineHeight: '20px', // 20px
                fontStyle: 'normal', // normal
            },

            body1: {
                fontFamily: fontFamily,
                fontSize: '16px !important', // 16px
                fontWeight: '400', // 500
                letterSpacing: '0.25px', // 0.25px
                lineHeight: '28px', // 28px
                fontStyle: 'normal', // normal
            },
            body2: {
                fontFamily: fontFamily,
                fontSize: '14px !important', // 14px
                fontWeight: '400', // 500
                letterSpacing: '0.25px', // 0.25px
                lineHeight: '20px', // 20px
                fontStyle: 'normal', // normal
            },
            caption: {
                fontFamily: fontFamily,
                fontSize: '12px !important', // 12px
                fontWeight: '400', // 500
                letterSpacing: '0.25px', // 0.25px
                lineHeight: '16px', // 20px
                fontStyle: 'normal', // normal
            },
            button: {
                fontFamily: fontFamily,
                fontSize: '14px !important', // 14px
                fontWeight: '700', // 700
                letterSpacing: '0.25px', // 0.25px
                lineHeight: '16px', // 16px
                fontStyle: 'normal', // normal
                textTransform: 'none', // none
            },
            overline: {
                fontFamily: fontFamily,
                fontSize: '12px !important', // 12px
                fontWeight: '500', // 500
                letterSpacing: '1.25px', // 1.25px
                lineHeight: '16px', // 16px
                fontStyle: 'normal', // normal
            },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: `
                    html, body, #root, .MuiTypography-root, .MuiButton-root, .MuiButtonBase-root, .MuiInputBase-root, .MuiMenuItem-root, .MuiListItem-root {
                        font-family: ${fontFamily} !important;
                    }
                `,
            },
            MuiStep: {
                styleOverrides: {
                    root: {
                        '& .MuiStepIcon-root': {
                            width: '2em',
                            height: '2em',
                        },
                        '& .MuiStepIcon-text': {
                            fontSize: '12px',
                        },
                    },
                },
            },

            MuiStepIcon: {
                styleOverrides: {
                    root: {
                        '&.Mui-completed': {
                            color: themeColors?.success.default,
                        },
                    },
                },
            },

            MuiAlert: {
                styleOverrides: {
                    root: {
                        '&.MuiAlert-filled': {
                            '&.MuiAlert-colorWarning': {
                                backgroundColor: themeColors?.warning[200],
                            },
                            '& .MuiAlert-icon': {
                                color: themeColors?.warning.default,
                            },
                        },
                        '&.MuiAlert-outlined': {
                            '&.MuiAlert-colorWarning': {
                                borderColor: themeColors?.warning[100],
                            },
                        },
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        '&.Mui-selected': {
                            fontWeight: 'bold',
                        },
                        fontStyle: 'italic',
                    },
                },
            },

            MuiPaginationItem: {
                styleOverrides: {
                    root: {
                        '&.Mui-selected:hover': {
                            backgroundColor: themeColors?.accent.default,
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        fontFamily: 'inherit',
                        fontSize: 'inherit',
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    root: {
                        fontFamily: `${fontFamily} !important`,
                    },
                },
                defaultProps: {
                    fontSize: 'inherit',
                    fontFamily: fontFamily,
                },
            },
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        fontSize: '12px',
                    },
                },
            },
            MuiPickersCalendarHeader: {
                styleOverrides: {
                    labelContainer: {
                        fontSize: '12px',
                    },
                },
            },
            MuiPickersMonth: {
                styleOverrides: {
                    monthButton: {
                        fontSize: '12px',
                    },
                },
            },
            MuiPickersYear: {
                styleOverrides: {
                    yearButton: {
                        fontSize: '12px',
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        fontSize: '12px',
                        backgroundColor: app === 'expertobayer' ? themeColors?.primary.default : undefined,
                    },
                    arrow: {
                        color: app === 'expertobayer' ? themeColors?.primary.default : undefined,
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        fontSize: '12px !important',
                    },
                },
            },
            MuiAccordionSummary: {
                styleOverrides: {
                    content: {
                        fontSize: '14px',
                        fontWeight: 'bold',
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    noOptions: {
                        color: 'black',
                        fontSize: '14px',
                    },
                    option: {
                        fontSize: '14px',
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        fontFamily: fontFamily,
                        borderRadius: 0,
                        fontSize: '14px',
                        fontWeight: 700,
                        letterSpacing: '0.25px',
                        lineHeight: '16px',
                        height: 'fit-content',
                    },
                    sizeSmall: {
                        padding: '8px 14px',
                    },
                    sizeMedium: {
                        padding: '10px 16px',
                    },
                    sizeLarge: {
                        padding: '12px 16px',
                    },
                    outlined: {
                        borderColor: themeColors?.mui.primary.colors.stroke,
                    },
                    text: {
                        fontWeight: 'bold',
                    },
                },
                defaultProps: {
                    disableElevation: true,
                },
                variants: [
                    {
                        props: { variant: 'outlined', color: 'gray' },
                        style: {
                            color: colors.gray[800],
                            borderColor: colors.gray[800],
                            '&:hover': {
                                borderColor: colors.gray[800],
                            },
                        },
                    },
                    {
                        props: { variant: 'contained', color: 'white' },
                        style: {
                            color: themeColors?.primary.default,
                            background: 'white',
                            '&:hover': { background: 'white' },
                        },
                    },
                    {
                        props: { variant: 'outlined', color: 'white' },
                        style: {
                            color: 'white',
                            borderColor: 'white',
                        },
                    },
                    {
                        props: { variant: 'text', color: 'white' },
                        style: {
                            color: 'white',
                        },
                    },
                    {
                        props: { variant: 'outlined', color: 'error' },
                        style: {
                            background: 'white',
                            '&:hover': { background: 'white' },
                        },
                    },
                ],
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        '&.MuiIconButton-sizeMedium': {
                            width: '48px',
                            height: '48px',
                            padding: '12px',
                        },
                        '&.MuiIconButton-sizeSmall': {
                            width: '32px',
                            height: '32px',
                            padding: '12px',
                        },
                        '& .MuiSvgIcon-root': {
                            width: '24px',
                            height: '24px',
                        },
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        '& .MuiIconButton-sizeSmall': {
                            width: 'unset !important',
                            height: 'unset !important',
                            padding: 'unset !important',
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontSize: '14px',
                        color: 'grey',
                        '&.Mui-focused': {
                            color: 'black',
                        },
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        fontSize: '14px',
                        borderRadius: '0 !important',
                    },
                },
            },
            /** Dense single-line fields: aligns TextField, Select, DatePicker (incl. MUI X pickers). */
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '&.MuiInputBase-sizeSmall:not(.MuiInputBase-multiline)': {
                            alignItems: 'center',
                            minHeight: 40,
                        },
                        /**
                         * Select shows the value in `div.MuiSelect-select`, not the hidden `input`;
                         * theme `input` rules alone do not apply. Mirror native input metrics here.
                         */
                        '& .MuiSelect-select': {
                            lineHeight: 1.4375,
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: 0,
                        },
                    },
                    input: {
                        lineHeight: 1.4375,
                        '&::placeholder': {
                            fontSize: '14px',
                        },
                    },
                },
            },

            MuiListItemText: {
                styleOverrides: {
                    root: {
                        fontSize: '12px !important',
                    },
                },
                defaultProps: {
                    disableTypography: true,
                },
            },

            MuiCheckbox: {
                variants: [
                    {
                        props: { color: 'info' },
                        style: {
                            //color: pink[800],
                            '&.Mui-checked': {
                                color: themeColors?.mui.primary.onSurface,
                            },
                        },
                    },
                ],
            },

            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        color: `${themeColors?.mui.primary.colors.inactive} !important`,
                        fontSize: '14px !important',
                    },
                },
            },
        },
    };

    const theme = MUIcreateTheme(parentTheme, themeOptions);

    return theme;
};

export default createTheme;
